/**
 * Utility functions for input masks
 */

/**
 * Mask for CPF format: XXX.XXX.XXX-XX
 */
export const maskCPF = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply mask
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
  if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
  return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
};

/**
 * Mask for CNPJ format: XX.XXX.XXX/XXXX-XX
 */
export const maskCNPJ = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply mask
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
  if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
  if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
  return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
};

/**
 * Mask for CPF or CNPJ (intelligent mask based on length)
 */
export const maskDocument = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  
  // If has up to 11 digits, apply CPF mask
  if (numbers.length <= 11) {
    return maskCPF(numbers);
  }
  
  // If has more, apply CNPJ mask
  return maskCNPJ(numbers);
};

/**
 * Mask for phone format: (XX)XXXX-XXXX or (XX)XXXXX-XXXX
 */
export const maskPhone = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply mask
  if (numbers.length === 0) return '';
  if (numbers.length <= 2) return `(${numbers}`;
  if (numbers.length <= 6) return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
  if (numbers.length <= 10) return `(${numbers.slice(0, 2)})${numbers.slice(2, 6)}-${numbers.slice(6)}`;
  return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

/**
 * Mask for CEP format: XXXXX-XXX
 */
export const maskCEP = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply mask
  if (numbers.length <= 5) return numbers;
  return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
};

/**
 * Remove all mask characters, returning only numbers
 */
export const unmask = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Validate email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate CPF format (basic check for 11 digits)
 */
export const validateCPF = (cpf: string): boolean => {
  const numbers = unmask(cpf);
  return numbers.length === 11;
};

/**
 * Validate CNPJ format (basic check for 14 digits)
 */
export const validateCNPJ = (cnpj: string): boolean => {
  const numbers = unmask(cnpj);
  return numbers.length === 14;
};

/**
 * Validate Document (CPF or CNPJ)
 */
export const validateDocument = (document: string): boolean => {
  const numbers = unmask(document);
  return numbers.length === 11 || numbers.length === 14;
};

/**
 * Validate CEP format (8 digits)
 */
export const validateCEP = (cep: string): boolean => {
  const numbers = unmask(cep);
  return numbers.length === 8;
};

/**
 * Validate phone format (10 or 11 digits)
 */
export const validatePhone = (phone: string): boolean => {
  const numbers = unmask(phone);
  return numbers.length === 10 || numbers.length === 11;
};
